/**
 * Story SF-CHAT-6.5 — sessão do app principal no frontend de teste, modo live.
 *
 * O chat nunca recebe senha: o login vai **direto** para a API do app
 * (`/api/v1/auth/login/` e, quando vier `requires_2fa`, `/login/2fa/`). O que
 * fica no navegador é só o token de acesso, em `sessionStorage` (morre com a
 * aba). O refresh token não é guardado: quando o acesso vencer, entra-se de novo.
 *
 * Antes de **cada** envio, `issueGrant()` pede ao app um grant de uso único
 * (`POST /api/v1/auth/chat/grants/`, Bearer). O grant vai no corpo do turno e
 * não é guardado em lugar nenhum.
 *
 * Tudo aqui é injetável (fetch, storage) para ser testado em Node sem rede.
 */

export const STORAGE_KEY = "sf-chat-app-session";

export interface AppUser {
  id: number;
  nome: string;
  empresaId: number | null;
  empresaNome: string | null;
}

export interface StoredSession {
  access: string;
  user: AppUser;
}

export type LoginResult =
  | { kind: "signed_in"; user: AppUser }
  | { kind: "needs_2fa"; challenge: string; expiresInSeconds: number | null };

/** Falha que a tela mostra como está: já vem em português e sem detalhe interno. */
export type AppSessionErrorKind =
  | "invalid_input"
  | "reauth" // o acesso venceu ou foi recusado — voltar ao login
  | "forbidden"
  | "rate_limited"
  | "unavailable"
  | "config";

export class AppSessionError extends Error {
  readonly kind: AppSessionErrorKind;

  constructor(message: string, kind: AppSessionErrorKind) {
    super(message);
    this.name = "AppSessionError";
    this.kind = kind;
  }
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

const UNAVAILABLE = "Não foi possível falar com o aplicativo agora. Verifique a conexão e tente de novo.";
const SESSION_EXPIRED = "Sua sessão no aplicativo expirou. Entre de novo para continuar a conversa.";

function memoryStorage(): StorageLike {
  const dados = new Map<string, string>();
  return {
    getItem: (k) => dados.get(k) ?? null,
    setItem: (k, v) => void dados.set(k, v),
    removeItem: (k) => void dados.delete(k),
  };
}

function toUser(raw: unknown): AppUser | null {
  if (!raw || typeof raw !== "object") return null;
  const u = raw as Record<string, unknown>;
  if (typeof u.id !== "number") return null;
  return {
    id: u.id,
    nome: typeof u.nome_completo === "string" && u.nome_completo ? u.nome_completo : String(u.email ?? ""),
    empresaId: typeof u.empresa_id === "number" ? u.empresa_id : null,
    empresaNome: typeof u.empresa_nome === "string" ? u.empresa_nome : null,
  };
}

/** Primeira mensagem legível de um erro DRF: `detail`, `non_field_errors` ou campo. */
function drfMessage(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  if (typeof b.detail === "string") return b.detail;
  for (const valor of Object.values(b)) {
    if (typeof valor === "string") return valor;
    if (Array.isArray(valor) && typeof valor[0] === "string") return valor[0];
  }
  return null;
}

export function createAppSession(options: {
  baseUrl: string | undefined;
  fetchImpl?: FetchLike;
  storage?: StorageLike;
}) {
  const base = (options.baseUrl ?? "").replace(/\/+$/, "");
  const doFetch: FetchLike = options.fetchImpl ?? ((i, init) => fetch(i, init));
  const storage = options.storage ?? memoryStorage();

  function requireBase(): string {
    if (!base) {
      throw new AppSessionError(
        "O endereço do aplicativo não está configurado neste ambiente (VITE_APP_API_BASE_URL).",
        "config",
      );
    }
    return base;
  }

  async function post(path: string, body: unknown, access?: string) {
    let resposta: Response;
    try {
      resposta = await doFetch(requireBase() + path, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
        },
        body: JSON.stringify(body),
      });
    } catch (erro) {
      if (erro instanceof AppSessionError) throw erro;
      throw new AppSessionError(UNAVAILABLE, "unavailable");
    }
    let corpo: unknown = null;
    try {
      corpo = await resposta.json();
    } catch {
      corpo = null;
    }
    return { status: resposta.status, corpo };
  }

  function current(): StoredSession | null {
    try {
      const bruto = storage.getItem(STORAGE_KEY);
      if (!bruto) return null;
      const salvo = JSON.parse(bruto) as StoredSession;
      return typeof salvo?.access === "string" && salvo.user ? salvo : null;
    } catch {
      return null;
    }
  }

  function signOut(): void {
    storage.removeItem(STORAGE_KEY);
  }

  function finish(corpo: unknown): LoginResult {
    const c = corpo as { tokens?: { access?: unknown }; user?: unknown } | null;
    const access = c?.tokens?.access;
    const user = toUser(c?.user);
    if (typeof access !== "string" || !user) {
      throw new AppSessionError("O aplicativo respondeu de um jeito inesperado. Tente de novo.", "unavailable");
    }
    // Só o acesso e o mínimo para a tela. Refresh token não é guardado.
    storage.setItem(STORAGE_KEY, JSON.stringify({ access, user } satisfies StoredSession));
    return { kind: "signed_in", user };
  }

  async function signIn(email: string, password: string): Promise<LoginResult> {
    if (!email.trim() || !password) {
      throw new AppSessionError("Informe e-mail e senha.", "invalid_input");
    }
    const { status, corpo } = await post("/api/v1/auth/login/", { email: email.trim(), password });
    if (status === 200 && (corpo as { requires_2fa?: unknown })?.requires_2fa === true) {
      const c = corpo as { challenge?: unknown; expires_in?: unknown };
      if (typeof c.challenge !== "string" || !c.challenge) {
        throw new AppSessionError("O aplicativo pediu o segundo fator sem enviar o desafio. Entre de novo.", "unavailable");
      }
      return {
        kind: "needs_2fa",
        challenge: c.challenge,
        expiresInSeconds: typeof c.expires_in === "number" ? c.expires_in : null,
      };
    }
    if (status === 200) return finish(corpo);
    if (status === 400) {
      throw new AppSessionError(drfMessage(corpo) ?? "E-mail ou senha incorretos.", "invalid_input");
    }
    if (status === 429) {
      throw new AppSessionError("Muitas tentativas. Espere alguns minutos e tente de novo.", "rate_limited");
    }
    throw new AppSessionError(UNAVAILABLE, "unavailable");
  }

  async function verifySecondFactor(challenge: string, code: string): Promise<LoginResult> {
    const limpo = code.replace(/\s+/g, "");
    if (!/^\d{6}$/.test(limpo)) {
      throw new AppSessionError("O código tem 6 dígitos, do seu app autenticador.", "invalid_input");
    }
    const { status, corpo } = await post("/api/v1/auth/login/2fa/", { challenge, code: limpo });
    if (status === 200) return finish(corpo);
    const codigo = (corpo as { code?: unknown })?.code;
    const mensagem = drfMessage(corpo);
    if (status === 429) {
      throw new AppSessionError(mensagem ?? "Muitas tentativas. Entre de novo com e-mail e senha.", "rate_limited");
    }
    if (status === 400 && codigo === "desafio_invalido") {
      // O desafio venceu: o código não adianta mais, é preciso recomeçar.
      throw new AppSessionError(mensagem ?? "O desafio expirou. Entre de novo com e-mail e senha.", "reauth");
    }
    if (status === 400) {
      throw new AppSessionError(mensagem ?? "Código inválido.", "invalid_input");
    }
    throw new AppSessionError(UNAVAILABLE, "unavailable");
  }

  /** Grant novo, de uso único, para **um** turno. Nunca é guardado. */
  async function issueGrant(): Promise<string> {
    const sessao = current();
    if (!sessao) throw new AppSessionError(SESSION_EXPIRED, "reauth");
    const corpo: Record<string, number> = {};
    if (sessao.user.empresaId !== null) corpo.empresa_id = sessao.user.empresaId;
    const { status, corpo: resposta } = await post("/api/v1/auth/chat/grants/", corpo, sessao.access);
    const grant = (resposta as { grant?: unknown })?.grant;
    if (status === 201 && typeof grant === "string" && grant) return grant;
    if (status === 401) {
      signOut();
      throw new AppSessionError(SESSION_EXPIRED, "reauth");
    }
    if (status === 403) {
      throw new AppSessionError(
        "O aplicativo não autorizou o chat para esta empresa. Confira seu acesso no aplicativo.",
        "forbidden",
      );
    }
    throw new AppSessionError(UNAVAILABLE, "unavailable");
  }

  /**
   * Story SF-CHAT-8.2: empresas que o app deixa este usuário acessar
   * (`GET /api/v1/empresas/`). Serve ao operador sem `empresa_vinculada`, que
   * precisa escolher a empresa antes do grant. O app continua decidindo o acesso.
   */
  async function listCompanies(): Promise<{ id: number; nome: string }[]> {
    const sessao = current();
    if (!sessao) throw new AppSessionError(SESSION_EXPIRED, "reauth");
    let resposta: Response;
    try {
      resposta = await doFetch(requireBase() + "/api/v1/empresas/", {
        headers: { Authorization: `Bearer ${sessao.access}` },
      });
    } catch (erro) {
      if (erro instanceof AppSessionError) throw erro;
      throw new AppSessionError(UNAVAILABLE, "unavailable");
    }
    if (resposta.status === 401) {
      signOut();
      throw new AppSessionError(SESSION_EXPIRED, "reauth");
    }
    if (!resposta.ok) throw new AppSessionError(UNAVAILABLE, "unavailable");
    const corpo = (await resposta.json().catch(() => null)) as { results?: unknown } | unknown[] | null;
    const lista = Array.isArray(corpo) ? corpo : Array.isArray((corpo as { results?: unknown })?.results) ? (corpo as { results: unknown[] }).results : [];
    return lista.flatMap((bruto) => {
      const e = bruto as Record<string, unknown>;
      if (!e || typeof e.id !== "number") return [];
      const nome = [e.nome_fantasia, e.razao_social, e.nome].find((v) => typeof v === "string" && v.trim()) as string | undefined;
      return [{ id: e.id, nome: nome ?? `Empresa ${e.id}` }];
    });
  }

  /** Grava a empresa escolhida na sessão da aba; o grant passa a levá-la. */
  function chooseCompany(empresa: { id: number; nome: string }): AppUser {
    const sessao = current();
    if (!sessao) throw new AppSessionError(SESSION_EXPIRED, "reauth");
    const user = { ...sessao.user, empresaId: empresa.id, empresaNome: empresa.nome };
    storage.setItem(STORAGE_KEY, JSON.stringify({ access: sessao.access, user } satisfies StoredSession));
    return user;
  }

  return { signIn, verifySecondFactor, issueGrant, current, signOut, listCompanies, chooseCompany };
}

export type AppSession = ReturnType<typeof createAppSession>;
